import React, { useState } from 'react';

interface PasswordInputProps {
  label: string;
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  autoComplete?: string;
  disabled?: boolean;
  forceLightMode?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

/**
 * Composant de saisie de mot de passe sécurisé avec icône de cadenas et option pour afficher/masquer le mot de passe
 */
const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  id,
  name,
  value,
  onChange,
  required = false,
  placeholder = '',
  className = '',
  autoComplete = 'current-password',
  disabled = false,
  forceLightMode = false,
  onPaste
}) => {
  // État pour contrôler la visibilité du mot de passe
  const [showPassword, setShowPassword] = useState(false);

  // Fonction pour basculer la visibilité du mot de passe
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={`relative ${className}`}>
      <label htmlFor={id} className={`block text-sm font-medium text-gray-700 ${!forceLightMode ? 'dark:text-gray-300' : ''} mb-1`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {/* Icône de cadenas à gauche */}
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className={`h-5 w-5 text-gray-400 ${!forceLightMode ? 'dark:text-gray-500' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
        </div>
        
        {/* Champ de saisie */}
        <input
          id={id}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onPaste={onPaste}
          required={required}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`w-full pl-10 pr-10 py-2 border border-gray-300 ${!forceLightMode ? 'dark:border-gray-700 dark:bg-gray-800 dark:text-white' : ''} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
        />
        
        {/* Bouton pour afficher/masquer le mot de passe */}
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={togglePasswordVisibility}
        >
          {showPassword ? (
            <svg className={`h-5 w-5 text-gray-500 ${!forceLightMode ? 'dark:text-gray-400' : ''} hover:text-gray-700 ${!forceLightMode ? 'dark:hover:text-gray-300' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className={`h-5 w-5 text-gray-500 ${!forceLightMode ? 'dark:text-gray-400' : ''} hover:text-gray-700 ${!forceLightMode ? 'dark:hover:text-gray-300' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
              <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default PasswordInput;
